/**
 * gallery-submit.js — TFS Wheels Customer Build Gallery Submission Form
 *
 * Self-contained vanilla-JS module. Mounts into #tfs-gallery-submit.
 *
 * Config via data-attributes on the container element:
 *   data-api-base           API origin (default: https://tfs-manager-server-production.up.railway.app)
 *   data-turnstile-sitekey  Cloudflare Turnstile sitekey (default: ALWAYS-PASS test key)
 *
 * ?token= query-param: if present, GET {api}/api/gallery/prefill?token= to pre-populate form.
 *
 * 3-STEP WIZARD (no visible progress indicator — sunk-cost design):
 *   Step 1 — Build submit:  POST /api/gallery/submissions (multipart) → { public_id, review_token }
 *   Step 2 — Review submit: POST /api/gallery/submissions/reviews   → { show_google_ask, ... }
 *   Step 3 — Done:          Google ask (if show_google_ask) or thank-you
 */

(function () {
  'use strict';

  // ============================================================
  // Constants
  // ============================================================
  const DEFAULT_API_BASE = 'https://tfs-manager-server-production.up.railway.app';
  const DEFAULT_SITEKEY  = '1x00000000000000000000AA'; // Turnstile ALWAYS-PASS test key
  const MAX_FILES        = 12;
  const MAX_FILE_BYTES   = 15 * 1024 * 1024; // 15 MB
  const ALLOWED_MIMES    = new Set(['image/jpeg','image/jpg','image/png','image/webp','image/heic','image/heif']);

  const FITMENT_TYPES = [
    { value: '',          label: 'Select fitment type...' },
    { value: 'stock',     label: 'Stock / No modification' },
    { value: 'lift',      label: 'Lifted' },
    { value: 'leveling',  label: 'Leveling kit' },
    { value: 'springs',   label: 'Spring upgrade' },
    { value: 'coilovers', label: 'Coilovers' },
    { value: 'lowered',   label: 'Lowered' },
    { value: 'other',     label: 'Other' },
  ];

  const FITMENT_DETAIL_PLACEHOLDER = {
    lift:      'e.g. 4in Rough Country, 285/70R17',
    leveling:  'e.g. 1.5in Rough Country leveling kit',
    springs:   'e.g. Old Man Emu springs +2in',
    coilovers: 'e.g. Bilstein 5100 series',
    lowered:   'e.g. 1.5in drop springs',
    other:     'Describe your setup...',
  };

  // ============================================================
  // State — module-level
  // ============================================================
  let _selectedFiles    = []; // Array of { file: File, previewUrl: string }
  let _turnstileToken   = null;
  let _turnstileWidgetId = null;
  let _hasTireBlock     = false;
  let _prefillData      = null;
  let _prefillToken     = null;
  let _publicId         = null;   // set after step-1 success
  let _reviewToken      = null;   // set after step-1 success
  let _hasWheel         = false;  // track whether wheel brand was entered
  let _hasTire          = false;  // track whether tire was expanded + filled

  // Product data carried into step 2 for review block labels/thumbnails/required logic
  // Each entry: { kind, is_resolved, resolved_title, resolved_image_url, label }
  let _wheelProductMeta = null;
  let _tireProductMeta  = null;

  // ============================================================
  // Entry point
  // ============================================================
  function init() {
    const root = document.getElementById('tfs-gallery-submit');
    if (!root) return;

    const apiBase = (root.dataset.apiBase || DEFAULT_API_BASE).replace(/\/$/, '');
    const sitekey = root.dataset.turnstileSitekey || DEFAULT_SITEKEY;

    root.textContent = '';
    root.appendChild(buildLoadingEl());

    const urlToken = new URLSearchParams(window.location.search).get('token') || '';

    if (urlToken) {
      _prefillToken = urlToken;
      fetchPrefill(apiBase, urlToken)
        .then((data) => { _prefillData = data; renderStep1(root, apiBase, sitekey); })
        .catch(() => { _prefillData = null; renderStep1(root, apiBase, sitekey); });
    } else {
      renderStep1(root, apiBase, sitekey);
    }
  }

  // ============================================================
  // Prefill fetch
  // ============================================================
  async function fetchPrefill(apiBase, token) {
    const resp = await fetch(`${apiBase}/api/gallery/prefill?token=${encodeURIComponent(token)}`, {
      headers: { Accept: 'application/json' },
    });
    if (!resp.ok) throw new Error('Prefill fetch failed');
    const json = await resp.json();
    if (!json.success) throw new Error(json.error || 'Prefill error');
    return json.data; // { buyer, vehicle, products }
  }

  // ============================================================
  // DOM helpers
  // ============================================================
  function buildLoadingEl() {
    const d = el('div', { class: 'gs-loading' });
    d.textContent = 'Loading...';
    return d;
  }

  function el(tag, attrs, ...children) {
    const node = document.createElement(tag);
    for (const [k, v] of Object.entries(attrs || {})) {
      if (k === 'class') node.className = v;
      else if (k.startsWith('on')) node.addEventListener(k.slice(2), v);
      else node.setAttribute(k, v);
    }
    for (const child of children) {
      if (child == null) continue;
      node.appendChild(typeof child === 'string' ? document.createTextNode(child) : child);
    }
    return node;
  }

  function fieldEl({ id, label, required, optional, hint, children }) {
    const wrap = el('div', { class: 'gs-field' });
    if (label) {
      const lbl = el('label', { class: 'gs-label', for: id || '' });
      lbl.textContent = label;
      if (required) {
        const sp = el('span', { class: 'gs-req' });
        sp.textContent = ' *';
        lbl.appendChild(sp);
      }
      if (optional) {
        const sp = el('span', { class: 'gs-opt' });
        sp.textContent = ' (optional)';
        lbl.appendChild(sp);
      }
      wrap.appendChild(lbl);
    }
    for (const c of (Array.isArray(children) ? children : [children])) {
      if (c) wrap.appendChild(c);
    }
    if (hint) {
      const h = el('div', { class: 'gs-hint' });
      h.textContent = hint;
      wrap.appendChild(h);
    }
    return wrap;
  }

  function inputEl(attrs) { return el('input', { class: 'gs-input', ...attrs }); }

  function selectEl(options, attrs) {
    const sel = el('select', { class: 'gs-select', ...attrs });
    for (const { value, label } of options) {
      const opt = el('option', { value });
      opt.textContent = label;
      sel.appendChild(opt);
    }
    return sel;
  }

  function textareaEl(attrs) { return el('textarea', { class: 'gs-textarea', ...attrs }); }

  function cardEl(title, badge, ...bodyNodes) {
    const card = el('div', { class: 'gs-card' });
    const titleNode = el('div', { class: 'gs-card-title' });
    titleNode.textContent = title;
    if (badge) {
      const b = el('span', { class: 'gs-badge' });
      b.textContent = badge;
      titleNode.appendChild(b);
    }
    card.appendChild(titleNode);
    for (const n of bodyNodes) { if (n) card.appendChild(n); }
    return card;
  }

  function row(...cols) {
    const r = el('div', { class: `gs-row gs-row-${cols.length}` });
    for (const c of cols) { if (c) r.appendChild(c); }
    return r;
  }

  function clearEl(node) {
    while (node.firstChild) node.removeChild(node.firstChild);
  }

  function showBanner(bannerEl, msg, type) {
    bannerEl.textContent = msg;
    bannerEl.className   = `gs-banner gs-banner-${type}`;
    bannerEl.style.display = '';
    bannerEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  function esc(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  // ============================================================
  // Star rating widget
  // ============================================================
  function starsEl(name, required) {
    const wrap = el('div', { class: 'gs-stars', role: 'radiogroup', 'aria-label': 'Star rating' });
    for (let v = 5; v >= 1; v--) {
      const uid = `${name}_${v}`;
      const inp = el('input', { type: 'radio', name, id: uid, value: String(v) });
      if (required) inp.setAttribute('required', '');
      const lbl = el('label', { for: uid, 'aria-label': `${v} star${v !== 1 ? 's' : ''}`, title: `${v} star${v !== 1 ? 's' : ''}` });
      lbl.textContent = '★'; // unicode star via textContent
      wrap.appendChild(inp);
      wrap.appendChild(lbl);
    }
    return wrap;
  }

  // ============================================================
  // Upload zone
  // ============================================================
  function buildUploadZone() {
    const zone = el('div', { class: 'gs-upload-zone', id: 'gs-upload-zone', tabindex: '0',
      role: 'button', 'aria-label': 'Upload photos of your build' });

    const fileInput = el('input', {
      type: 'file',
      accept: 'image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif',
      multiple: '',
      id: 'gs-file-input',
      'aria-label': 'Upload photos',
      tabindex: '-1',
    });

    const iconWrap = el('div', { class: 'gs-upload-icon-wrap' });
    const iconSvg = el('span', { class: 'gs-upload-icon', 'aria-hidden': 'true' });
    iconSvg.textContent = '📷'; // camera emoji via textContent
    iconWrap.appendChild(iconSvg);

    const lbl = el('div', { class: 'gs-upload-label' });
    const lblStrong = document.createElement('strong');
    lblStrong.textContent = 'Tap to upload photos';
    lbl.appendChild(lblStrong);
    lbl.appendChild(document.createTextNode(' or drag & drop'));

    const meta = el('div', { class: 'gs-upload-meta' });
    meta.textContent = 'JPEG, PNG, WebP or HEIC · Max 15 MB per photo · Up to 12 photos';

    zone.appendChild(fileInput);
    zone.appendChild(iconWrap);
    zone.appendChild(lbl);
    zone.appendChild(meta);

    // Allow keyboard activation
    zone.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileInput.click(); }
    });

    return zone;
  }

  // ============================================================
  // STEP 1 — Build form
  // ============================================================
  function renderStep1(root, apiBase, sitekey) {
    clearEl(root);

    const pf = _prefillData;

    // Prefill banner
    if (pf) {
      const banner = el('div', { class: 'gs-prefill-banner' });
      const checkIcon = el('span', { 'aria-hidden': 'true' });
      checkIcon.textContent = '✅'; // check emoji
      banner.appendChild(checkIcon);
      const name = String(pf.buyer?.name || '');
      if (name) {
        banner.appendChild(document.createTextNode(' Hi '));
        const strong = el('strong');
        strong.textContent = name;
        banner.appendChild(strong);
        banner.appendChild(document.createTextNode('! Your order info has been pre-filled — just add photos and submit.'));
      } else {
        banner.appendChild(document.createTextNode(' Your order info has been pre-filled — just add photos and submit.'));
      }
      root.appendChild(banner);
    }

    // No step progress indicator — intentionally omitted (sunk-cost design)

    const stepHeader = el('div', { class: 'gs-step-header' });
    const stepTitle = el('h2', { class: 'gs-step-title' });
    stepTitle.textContent = 'Share Your Build';
    const stepSub = el('p', { class: 'gs-step-sub' });
    stepSub.textContent = 'Tell us about your vehicle and the wheels you chose. We love seeing what our customers build.';
    stepHeader.appendChild(stepTitle);
    stepHeader.appendChild(stepSub);
    root.appendChild(stepHeader);

    const form = el('form', { id: 'gs-form', novalidate: '', enctype: 'multipart/form-data' });

    // ---- VEHICLE ----
    const vYear  = inputEl({ type: 'number', id: 'gs-vehicle-year',  name: 'vehicle_year',  placeholder: '2022', min: '1970', max: '2030', required: '' });
    const vMake  = inputEl({ type: 'text',   id: 'gs-vehicle-make',  name: 'vehicle_make',  placeholder: 'e.g. Ford', required: '', autocomplete: 'off' });
    const vModel = inputEl({ type: 'text',   id: 'gs-vehicle-model', name: 'vehicle_model', placeholder: 'e.g. F-150', required: '', autocomplete: 'off' });
    const vSub   = inputEl({ type: 'text',   id: 'gs-vehicle-sub',   name: 'vehicle_submodel', placeholder: 'e.g. XLT, Sport' });

    if (pf?.vehicle) {
      const v = pf.vehicle;
      if (v.year)  vYear.value  = v.year;
      if (v.make)  vMake.value  = v.make;
      if (v.model) vModel.value = v.model;
      if (v.trim)  vSub.value   = v.trim;
    }

    form.appendChild(cardEl('Vehicle', 'Required',
      row(
        fieldEl({ id: 'gs-vehicle-year',  label: 'Year',             required: true,  children: vYear  }),
        fieldEl({ id: 'gs-vehicle-make',  label: 'Make',             required: true,  children: vMake  }),
        fieldEl({ id: 'gs-vehicle-model', label: 'Model',            required: true,  children: vModel }),
        fieldEl({ id: 'gs-vehicle-sub',   label: 'Trim / Sub-model', optional: true,  children: vSub   })
      )
    ));

    // ---- WHEELS ----
    const wBrand  = inputEl({ type: 'text', id: 'gs-wheel-brand',  name: 'wheel_brand',       placeholder: 'e.g. Fuel, Method' });
    const wModel  = inputEl({ type: 'text', id: 'gs-wheel-model',  name: 'wheel_model',       placeholder: 'e.g. Maverick' });
    const wDiam   = inputEl({ type: 'text', id: 'gs-wheel-diam',   name: 'wheel_diameter',    placeholder: 'e.g. 20' });
    const wWidth  = inputEl({ type: 'text', id: 'gs-wheel-width',  name: 'wheel_width',       placeholder: 'e.g. 10' });
    const wFinish = inputEl({ type: 'text', id: 'gs-wheel-finish', name: 'wheel_finish',      placeholder: 'e.g. Matte Black Bronze' });
    const wBolt   = inputEl({ type: 'text', id: 'gs-wheel-bolt',   name: 'wheel_bolt_pattern',placeholder: 'e.g. 6x135' });

    if (pf?.products) {
      const wp = pf.products.find((p) => p.kind === 'wheel');
      if (wp) {
        const wBrandVal = wp.brand || wp.resolved_vendor || wp.vendor;
        const wModelVal = wp.model || wp.wheel_model;
        if (wBrandVal)       wBrand.value  = wBrandVal;
        if (wModelVal)       wModel.value  = wModelVal;
        if (wp.diameter)     wDiam.value   = wp.diameter;
        if (wp.width)        wWidth.value  = wp.width;
        if (wp.finish)       wFinish.value = wp.finish;
        if (wp.bolt_pattern) wBolt.value   = wp.bolt_pattern;
      }
    }

    // Tire toggle
    function setTireToggleLabel(btn, adding) {
      btn.textContent = '';
      const sym = document.createElement('span');
      sym.textContent = adding ? '+' : '−'; // minus sign
      btn.appendChild(sym);
      btn.appendChild(document.createTextNode(adding ? ' Add tire details' : ' Remove tire details'));
    }

    const tireToggleBtn = el('button', { type: 'button', class: 'gs-expander-btn', id: 'gs-tire-toggle' });
    setTireToggleLabel(tireToggleBtn, true);

    const tireSection = el('div', { class: 'gs-expander-section', id: 'gs-tire-section', hidden: '' });
    const tBrand = inputEl({ type: 'text', id: 'gs-tire-brand', name: 'tire_brand', placeholder: 'e.g. Nitto, Falken' });
    const tModel = inputEl({ type: 'text', id: 'gs-tire-model', name: 'tire_model', placeholder: 'e.g. Terra Grappler G2' });
    const tSize  = inputEl({ type: 'text', id: 'gs-tire-size',  name: 'tire_size',  placeholder: 'e.g. 285/70R17' });

    if (pf?.products) {
      const tp = pf.products.find((p) => p.kind === 'tire');
      if (tp) {
        const tBrandVal = tp.brand || tp.resolved_vendor || tp.vendor;
        const tModelVal = tp.model || tp.tire_model || tp.wheel_model;
        if (tBrandVal)    tBrand.value = tBrandVal;
        if (tModelVal)    tModel.value = tModelVal;
        if (tp.tire_size) tSize.value  = tp.tire_size;
        _hasTireBlock = true;
      }
    }

    tireSection.appendChild(row(
      fieldEl({ id: 'gs-tire-brand', label: 'Tire Brand', optional: true, children: tBrand }),
      fieldEl({ id: 'gs-tire-model', label: 'Tire Model', optional: true, children: tModel }),
      fieldEl({ id: 'gs-tire-size',  label: 'Tire Size',  optional: true, children: tSize  })
    ));

    tireToggleBtn.addEventListener('click', () => {
      _hasTireBlock = !_hasTireBlock;
      if (_hasTireBlock) {
        tireSection.removeAttribute('hidden');
        setTireToggleLabel(tireToggleBtn, false);
      } else {
        tireSection.setAttribute('hidden', '');
        setTireToggleLabel(tireToggleBtn, true);
        tBrand.value = '';
        tModel.value = '';
        tSize.value  = '';
      }
      liveValidateStep1(form, step1SubmitBtn, consentReview, consentUgc);
    });

    if (_hasTireBlock) {
      tireSection.removeAttribute('hidden');
      setTireToggleLabel(tireToggleBtn, false);
    }

    form.appendChild(cardEl('Wheels', 'Required',
      row(
        fieldEl({ id: 'gs-wheel-brand',  label: 'Brand',       required: true,  children: wBrand  }),
        fieldEl({ id: 'gs-wheel-model',  label: 'Model',       optional: true,  children: wModel  }),
        fieldEl({ id: 'gs-wheel-diam',   label: 'Diameter',    optional: true,  children: wDiam   }),
        fieldEl({ id: 'gs-wheel-width',  label: 'Width',       optional: true,  children: wWidth  })
      ),
      row(
        fieldEl({ id: 'gs-wheel-finish', label: 'Finish',      optional: true,  children: wFinish }),
        fieldEl({ id: 'gs-wheel-bolt',   label: 'Bolt Pattern',optional: true,  children: wBolt   })
      ),
      tireToggleBtn,
      tireSection
    ));

    // ---- FITMENT ----
    const fitSel = selectEl(FITMENT_TYPES, { id: 'gs-fitment-type', name: 'fitment_type' });
    const fitDetailRow = el('div', { class: 'gs-fitment-detail-row', id: 'gs-fitment-detail-row', hidden: '' });
    const fitDetail = inputEl({ type: 'text', id: 'gs-fitment-detail', name: 'fitment_detail', placeholder: 'Lift/mod details' });
    fitDetailRow.appendChild(fieldEl({ id: 'gs-fitment-detail', label: 'Modification Details', optional: true, children: fitDetail }));
    const fitNotes = textareaEl({ id: 'gs-fitment-notes', name: 'fitment_notes', placeholder: 'Any other fitment notes, offset info, spacers, etc.' });

    fitSel.addEventListener('change', () => {
      const v = fitSel.value;
      if (v && v !== 'stock') {
        fitDetailRow.removeAttribute('hidden');
        fitDetail.placeholder = FITMENT_DETAIL_PLACEHOLDER[v] || 'Describe your modification...';
      } else {
        fitDetailRow.setAttribute('hidden', '');
        fitDetail.value = '';
      }
    });

    form.appendChild(cardEl('Fitment Setup', 'Optional',
      fieldEl({ id: 'gs-fitment-type',  label: 'Setup Type',       optional: true, children: fitSel    }),
      fitDetailRow,
      fieldEl({ id: 'gs-fitment-notes', label: 'Additional Notes', optional: true, children: fitNotes  })
    ));

    // ---- PHOTOS ----
    const uploadZone = buildUploadZone();
    const thumbGrid  = el('div', { class: 'gs-thumb-grid', id: 'gs-thumb-grid' });
    const thumbCount = el('div', { class: 'gs-thumb-count', id: 'gs-thumb-count' });
    const photoError = el('span', { class: 'gs-field-error', id: 'gs-photo-error', style: 'display:none' });
    photoError.textContent = 'Please add at least one photo.';

    const fileInput = uploadZone.querySelector('input[type="file"]');
    fileInput.addEventListener('change', (e) => {
      handleFileSelect(Array.from(e.target.files || []), thumbGrid, thumbCount, fileInput);
      liveValidateStep1(form, step1SubmitBtn, consentReview, consentUgc);
    });

    uploadZone.addEventListener('dragover',  (e) => { e.preventDefault(); uploadZone.classList.add('gs-drag-over'); });
    uploadZone.addEventListener('dragleave', ()  => { uploadZone.classList.remove('gs-drag-over'); });
    uploadZone.addEventListener('drop', (e) => {
      e.preventDefault();
      uploadZone.classList.remove('gs-drag-over');
      handleFileSelect(Array.from(e.dataTransfer?.files || []), thumbGrid, thumbCount, fileInput);
      liveValidateStep1(form, step1SubmitBtn, consentReview, consentUgc);
    });

    form.appendChild(cardEl('Photos', 'Required — min 1',
      uploadZone,
      thumbGrid,
      thumbCount,
      photoError
    ));

    // ---- ABOUT YOU ----
    const bName  = inputEl({ type: 'text',  id: 'gs-buyer-name',  name: 'buyer_name',       placeholder: 'Your name',       required: '' });
    const bEmail = inputEl({ type: 'email', id: 'gs-buyer-email', name: 'buyer_email',       placeholder: 'your@email.com', required: '', autocomplete: 'email' });
    const bOrder = inputEl({ type: 'text',  id: 'gs-order-num',   name: 'order_number',      placeholder: '#62424' });
    const bInsta = inputEl({ type: 'text',  id: 'gs-instagram',   name: 'instagram_handle',  placeholder: '@yourhandle' });

    if (pf?.buyer) {
      const b = pf.buyer;
      if (b.name)         bName.value  = b.name;
      if (b.email)        bEmail.value = b.email;
      if (b.order_number) bOrder.value = b.order_number;
    }

    // IG-follow bonus opt-in checkbox (Change A)
    const igFollowChk = el('input', { type: 'checkbox', id: 'gs-followed-ig', name: 'followed_ig', value: '1' });
    const igFollowConsent = el('div', { class: 'gs-consent' });
    igFollowConsent.appendChild(igFollowChk);
    const igFollowTxt = el('span', { class: 'gs-consent-text' });
    igFollowTxt.textContent = 'I follow @tfswheels on Instagram (we tag featured builds!)';
    igFollowConsent.appendChild(igFollowTxt);

    form.appendChild(cardEl('About You', null,
      row(
        fieldEl({ id: 'gs-buyer-name',  label: 'Your Name',     required: true, children: bName  }),
        fieldEl({ id: 'gs-buyer-email', label: 'Email Address', required: true, children: bEmail })
      ),
      row(
        fieldEl({ id: 'gs-order-num', label: 'Order Number', optional: true, hint: 'Helps us verify your purchase', children: bOrder }),
        fieldEl({ id: 'gs-instagram', label: 'Instagram',    optional: true, hint: 'e.g. @tfsbuilds — for credit',  children: bInsta })
      ),
      igFollowConsent
    ));

    // ---- CONSENT ----
    const consentReview = el('input', { type: 'checkbox', id: 'gs-consent-review', name: 'consent_review_authorized', value: '1' });
    const consentUgc    = el('input', { type: 'checkbox', id: 'gs-consent-ugc',    name: 'consent_ugc_license',       value: '1' });
    consentReview.checked = true;
    consentUgc.checked    = true;

    const cr = el('div', { class: 'gs-consent' });
    cr.appendChild(consentReview);
    const crTxt = el('span', { class: 'gs-consent-text' });
    crTxt.textContent = 'I authorize TFS Wheels to publish my review on their website and product pages.';
    cr.appendChild(crTxt);

    const cu = el('div', { class: 'gs-consent' });
    cu.appendChild(consentUgc);
    const cuTxt = el('span', { class: 'gs-consent-text' });
    cuTxt.textContent = 'I grant TFS Wheels a license to use my submitted photos for marketing and promotional purposes.';
    cu.appendChild(cuTxt);

    form.appendChild(cardEl('Permissions', null, cr, cu));

    // ---- TURNSTILE ----
    const tsWrap = el('div', { class: 'gs-card', id: 'gs-turnstile-card' });
    const tsTitle = el('div', { class: 'gs-card-title' });
    tsTitle.textContent = 'Security Check';
    const tsContainer = el('div', { class: 'gs-turnstile', id: 'gs-turnstile' });
    tsWrap.appendChild(tsTitle);
    tsWrap.appendChild(tsContainer);
    form.appendChild(tsWrap);

    // ---- SUBMIT ----
    const submitBanner = el('div', { id: 'gs-submit-banner', class: 'gs-banner gs-banner-error', style: 'display:none' });
    const step1SubmitBtn = el('button', { type: 'submit', class: 'gs-submit-btn', id: 'gs-submit-btn', disabled: '' });
    step1SubmitBtn.textContent = 'Submit My Build';

    form.appendChild(submitBanner);
    form.appendChild(step1SubmitBtn);

    // Wire form events
    form.addEventListener('input',  () => liveValidateStep1(form, step1SubmitBtn, consentReview, consentUgc));
    form.addEventListener('change', () => liveValidateStep1(form, step1SubmitBtn, consentReview, consentUgc));
    consentReview.addEventListener('change', () => liveValidateStep1(form, step1SubmitBtn, consentReview, consentUgc));
    consentUgc.addEventListener('change',    () => liveValidateStep1(form, step1SubmitBtn, consentReview, consentUgc));

    form.addEventListener('submit', (e) => {
      e.preventDefault();
      handleStep1Submit({
        form, apiBase, sitekey, root,
        fields: { vYear, vMake, vModel, bName, bEmail },
        photoError, submitBanner,
        submitBtn: step1SubmitBtn,
        consentReview, consentUgc,
        wBrand, wModel, wDiam, wWidth, wFinish,
        tBrand, tModel, tSize,
      });
    });

    root.appendChild(form);

    loadTurnstile(sitekey, tsContainer, step1SubmitBtn, form, consentReview, consentUgc);
  }

  // ============================================================
  // Step 1 — Live validation
  // ============================================================
  function liveValidateStep1(form, submitBtn, consentReview, consentUgc) {
    if (!form) return;
    const vYear  = form.querySelector('[name="vehicle_year"]');
    const vMake  = form.querySelector('[name="vehicle_make"]');
    const vModel = form.querySelector('[name="vehicle_model"]');
    const wBrand = form.querySelector('[name="wheel_brand"]');
    const bName  = form.querySelector('[name="buyer_name"]');
    const bEmail = form.querySelector('[name="buyer_email"]');

    const vehicleOk = (vYear?.value || '').trim() &&
                      (vMake?.value || '').trim() &&
                      (vModel?.value || '').trim();
    const wheelOk   = (wBrand?.value || '').trim().length > 0;
    const buyerOk   = (bName?.value || '').trim() &&
                      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((bEmail?.value || '').trim());
    const photoOk   = _selectedFiles.length > 0;
    const tsOk      = !!_turnstileToken;
    const conOk     = (consentReview?.checked !== false) && (consentUgc?.checked !== false);

    submitBtn.disabled = !(vehicleOk && wheelOk && buyerOk && photoOk && tsOk && conOk);
  }

  // ============================================================
  // Step 1 — Submit handler
  // ============================================================
  async function handleStep1Submit({ form, apiBase, root, fields, photoError, submitBanner,
    submitBtn, consentReview, consentUgc,
    wBrand, wModel, wDiam, wWidth, wFinish,
    tBrand, tModel, tSize }) {

    const errs = [];

    if (!_selectedFiles.length) {
      photoError.style.display = '';
      errs.push('Please add at least one photo.');
    } else {
      photoError.style.display = 'none';
    }
    if (!_turnstileToken) errs.push('Please complete the security challenge.');
    if (!consentReview.checked || !consentUgc.checked) errs.push('Please accept both consent checkboxes.');

    if (errs.length) {
      showBanner(submitBanner, errs.join(' '), 'error');
      return;
    }

    // Track wheel/tire presence for step-2 review blocks
    _hasWheel = !!(wBrand?.value || '').trim();
    _hasTire  = _hasTireBlock && (
      !!(tBrand?.value || '').trim() ||
      !!(tModel?.value || '').trim() ||
      !!(tSize?.value  || '').trim()
    );

    // Build product meta for review block labels / required logic
    // Try to get resolved product from prefill; fall back to entered text
    if (_hasWheel) {
      const pfWheel = _prefillData?.products?.find((p) => p.kind === 'wheel');
      const isResolved = !!(pfWheel && pfWheel.resolved_product_id);
      let label;
      if (isResolved && pfWheel.resolved_title) {
        label = pfWheel.resolved_title;
      } else {
        // Build from entered text
        const parts = [
          (wBrand?.value || '').trim(),
          (wModel?.value || '').trim(),
          (wDiam?.value  || '').trim() ? (wDiam.value.trim() + '"') : '',
          (wWidth?.value || '').trim() ? (wWidth.value.trim() + 'w') : '',
          (wFinish?.value|| '').trim(),
        ].filter(Boolean);
        label = parts.join(' ') || 'Your Wheels';
      }
      _wheelProductMeta = {
        kind: 'wheel',
        is_resolved: isResolved,
        resolved_title: pfWheel?.resolved_title || null,
        resolved_image_url: (isResolved && pfWheel?.resolved_image_url) ? pfWheel.resolved_image_url : null,
        label,
      };
    } else {
      _wheelProductMeta = null;
    }

    if (_hasTire) {
      const pfTire = _prefillData?.products?.find((p) => p.kind === 'tire');
      const isResolved = !!(pfTire && pfTire.resolved_product_id);
      let label;
      if (isResolved && pfTire.resolved_title) {
        label = pfTire.resolved_title;
      } else {
        const parts = [
          (tBrand?.value || '').trim(),
          (tModel?.value || '').trim(),
          (tSize?.value  || '').trim(),
        ].filter(Boolean);
        label = parts.join(' ') || 'Your Tires';
      }
      _tireProductMeta = {
        kind: 'tire',
        is_resolved: isResolved,
        resolved_title: pfTire?.resolved_title || null,
        resolved_image_url: (isResolved && pfTire?.resolved_image_url) ? pfTire.resolved_image_url : null,
        label,
      };
    } else {
      _tireProductMeta = null;
    }

    submitBtn.disabled = true;
    submitBtn.textContent = '';
    const spinnerEl = document.createElement('span');
    spinnerEl.className = 'gs-spinner';
    submitBtn.appendChild(spinnerEl);
    submitBtn.appendChild(document.createTextNode(' Submitting…'));
    submitBanner.style.display = 'none';

    try {
      const fd = new FormData(form);

      fd.set('cf_turnstile_response', _turnstileToken);
      if (_prefillToken) fd.set('prefill_token', _prefillToken);
      fd.set('source', _prefillToken ? 'email_link' : 'cold');

      fd.delete('images');
      for (const { file } of _selectedFiles) {
        fd.append('images', file, file.name);
      }

      const resp = await fetch(`${apiBase}/api/gallery/submissions`, {
        method: 'POST',
        body: fd,
      });

      const json = await resp.json().catch(() => ({ success: false, error: 'Invalid server response' }));

      if (!resp.ok || !json.success) {
        throw new Error(json.error || `Server error (${resp.status})`);
      }

      // Store tokens for step 2
      _publicId    = json.public_id    || null;
      _reviewToken = json.review_token || null;

      // Advance to step 2
      renderStep2(root, apiBase);

    } catch (err) {
      console.error('[GallerySubmit] step1 error:', err);
      showBanner(submitBanner, err.message || 'Something went wrong. Please try again.', 'error');
      submitBtn.disabled    = false;
      submitBtn.textContent = 'Submit My Build';

      if (_turnstileWidgetId !== null && window.turnstile) {
        window.turnstile.reset(_turnstileWidgetId);
        _turnstileToken = null;
      }
    }
  }

  // ============================================================
  // Scroll helper — brings the top of the form into view with breathing room
  // for any sticky header the theme may have.
  // ============================================================
  function scrollFormToTop(root) {
    try {
      const top = root.getBoundingClientRect().top + window.pageYOffset - 24;
      window.scrollTo({ top: top < 0 ? 0 : top, behavior: 'smooth' });
    } catch (e) {
      try { root.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (e2) {}
    }
  }

  // ============================================================
  // STEP 2 — Review form
  // ============================================================
  function renderStep2(root, apiBase) {
    clearEl(root);

    // No step progress indicator — intentionally omitted

    const stepHeader = el('div', { class: 'gs-step-header gs-step-header-celebrate' });
    const stepIcon = el('div', { class: 'gs-celebrate-icon', 'aria-hidden': 'true' });
    stepIcon.textContent = '🚀'; // rocket — "let's get it live" (NOT a completion celebration)
    const stepTitle = el('h2', { class: 'gs-step-title' });
    stepTitle.textContent = "Your build's entered! Leave a review (optional)";
    const stepSub = el('p', { class: 'gs-step-sub' });
    stepSub.textContent = "Your photos are submitted and you're entered in this month's $200 draw. Leave a review below to help other customers (optional)."; // Change C: entry secured; review is optional (not a bonus entry — all entries are equal)
    stepHeader.appendChild(stepIcon);
    stepHeader.appendChild(stepTitle);
    stepHeader.appendChild(stepSub);
    root.appendChild(stepHeader);

    // If no review_token (shouldn't happen but safeguard), go straight to step 3
    if (!_reviewToken) {
      renderStep3(root, apiBase, {});
      return;
    }

    const reviewBlocks = {};

    if (_hasWheel && _wheelProductMeta) {
      reviewBlocks.product_wheel = buildReviewBlock('product_wheel', _wheelProductMeta);
    }
    if (_hasTire && _tireProductMeta) {
      reviewBlocks.product_tire  = buildReviewBlock('product_tire',  _tireProductMeta);
    }
    // Store review is always present and always required
    reviewBlocks.store = buildReviewBlock('store', {
      kind: 'store',
      is_resolved: true,   // store review is always required
      resolved_title: null,
      resolved_image_url: null,
      label: 'Your experience with TFS Wheels',
    });

    const reviewCard = el('div', { class: 'gs-card' });
    const reviewCardTitle = el('div', { class: 'gs-card-title' });
    reviewCardTitle.textContent = 'Your Reviews';
    reviewCard.appendChild(reviewCardTitle);
    for (const key of Object.keys(reviewBlocks)) {
      reviewCard.appendChild(reviewBlocks[key].el);
    }
    root.appendChild(reviewCard);

    const submitBanner = el('div', { id: 'gs-review-banner', class: 'gs-banner gs-banner-error', style: 'display:none' });
    const reviewSubmitBtn = el('button', { type: 'button', class: 'gs-submit-btn', id: 'gs-review-submit-btn' });
    reviewSubmitBtn.textContent = 'Submit Review';

    // Skip link
    const skipWrap = el('div', { class: 'gs-skip-wrap' });
    const skipBtn  = el('button', { type: 'button', class: 'gs-skip-btn', id: 'gs-skip-review-btn' });
    skipBtn.textContent = "Skip — my build's already entered →"; // Change C: entry is always secured
    skipBtn.addEventListener('click', () => renderStep3(root, apiBase, {}));
    skipWrap.appendChild(skipBtn);

    root.appendChild(submitBanner);
    root.appendChild(reviewSubmitBtn);
    root.appendChild(skipWrap);

    reviewSubmitBtn.addEventListener('click', () => {
      handleStep2Submit({ root, apiBase, reviewBlocks, submitBanner, submitBtn: reviewSubmitBtn });
    });

    scrollFormToTop(root);
  }

  // ============================================================
  // Review block builder
  // productMeta: { kind, is_resolved, resolved_title, resolved_image_url, label }
  // ============================================================
  function buildReviewBlock(kind, productMeta) {
    const block = el('div', { class: 'gs-review-block' });

    // --- Header: thumbnail (if resolved) + label ---
    const header = el('div', { class: 'gs-review-block-header' });

    if (productMeta.is_resolved && productMeta.resolved_image_url) {
      // Show product thumbnail
      const thumb = el('img', {
        class: 'gs-review-block-thumb',
        src: productMeta.resolved_image_url,
        alt: productMeta.resolved_title || '',
        loading: 'lazy',
      });
      header.appendChild(thumb);
    }

    const labelWrap = el('div', { class: 'gs-review-block-label-wrap' });

    if (kind === 'store') {
      // Store review — simple header without product context
      const ttl = el('div', { class: 'gs-review-block-title' });
      const starIcon = el('span', { class: 'gs-review-block-icon', 'aria-hidden': 'true' });
      starIcon.textContent = '★';
      ttl.appendChild(starIcon);
      ttl.appendChild(document.createTextNode(' ' + productMeta.label));
      labelWrap.appendChild(ttl);
    } else {
      // Product review — show "Review your [product]"
      const prompt = el('div', { class: 'gs-review-block-prompt' });
      prompt.textContent = 'Review your';
      labelWrap.appendChild(prompt);

      const ttl = el('div', { class: 'gs-review-block-title' });
      ttl.textContent = productMeta.label;
      labelWrap.appendChild(ttl);

      // Show optional badge if unresolved (customer-provided product)
      if (!productMeta.is_resolved) {
        const optBadge = el('span', { class: 'gs-review-block-optional-badge' });
        optBadge.textContent = 'Optional';
        labelWrap.appendChild(optBadge);
      }
    }

    header.appendChild(labelWrap);
    block.appendChild(header);

    // --- Rating stars — optional (Change C: review is never a gate for entry) ---
    const starName = `review_${kind.replace('product_', '')}_rating`;
    const isRequired = productMeta.is_resolved;
    const stars = starsEl(starName, false); // always optional — entry is secured by photos

    const reviewTitle = inputEl({
      type: 'text',
      name: `review_${kind.replace('product_', '')}_title`,
      placeholder: 'Review title (optional)',
    });

    const reviewBody = textareaEl({
      name: `review_${kind.replace('product_', '')}_body`,
      placeholder: 'Share your experience...',
    });

    const bodyErrorEl = el('div', {
      class: 'gs-field-error',
      id: `gs-body-error-${kind}`,
      style: 'display:none; margin-top:4px',
    });
    bodyErrorEl.textContent = 'Please write a review body — it is required when a rating is given.';

    function validateBody() {
      const hasRating = Array.from(stars.querySelectorAll('input[type="radio"]')).some((r) => r.checked);
      const hasBody   = reviewBody.value.trim().length > 0;
      if (hasRating && !hasBody) {
        bodyErrorEl.style.display = '';
        reviewBody.setAttribute('aria-invalid', 'true');
      } else {
        bodyErrorEl.style.display = 'none';
        reviewBody.removeAttribute('aria-invalid');
      }
    }

    stars.addEventListener('change', validateBody);
    reviewBody.addEventListener('input', validateBody);
    reviewBody.addEventListener('blur', validateBody);

    const ratingLabel = isRequired ? 'Rating' : 'Rating';
    block.appendChild(fieldEl({ label: ratingLabel, required: isRequired, optional: !isRequired, children: stars }));
    block.appendChild(fieldEl({ label: 'Title',  optional: true,      children: reviewTitle }));
    block.appendChild(fieldEl({ label: 'Review', required: isRequired, optional: !isRequired, children: reviewBody  }));
    block.appendChild(bodyErrorEl);

    return { el: block, stars, reviewTitle, reviewBody, bodyErrorEl, kind, isRequired };
  }

  // ============================================================
  // Step 2 — Submit handler
  // ============================================================
  async function handleStep2Submit({ root, apiBase, reviewBlocks, submitBanner, submitBtn }) {
    const errs = [];
    const reviews = [];

    for (const key of Object.keys(reviewBlocks)) {
      const block = reviewBlocks[key];
      const ratingInput = Array.from(block.stars.querySelectorAll('input[type="radio"]')).find((r) => r.checked);

      // Change C: reviews are optional — skip any block with no rating (entry already secured)
      if (!ratingInput) {
        continue;
      }

      const rating = parseInt(ratingInput.value, 10);
      const body   = block.reviewBody.value.trim();

      if (!body) {
        block.bodyErrorEl.style.display = '';
        block.reviewBody.setAttribute('aria-invalid', 'true');
        errs.push(`Please write a review body for: ${key.replace('product_', '')}.`);
        continue;
      }

      reviews.push({
        kind:   block.kind,
        rating,
        title:  block.reviewTitle.value.trim() || undefined,
        body,
      });
    }

    if (errs.length) {
      showBanner(submitBanner, errs.join(' '), 'error');
      try { submitBanner.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); } catch (e) {}
      return;
    }

    // Change C: reviews are OPTIONAL. If nothing was rated, "Submit" behaves
    // exactly like Skip — the build is already entered via photos, and the
    // reviews endpoint rejects an empty array, so go straight to the done step.
    if (reviews.length === 0) {
      renderStep3(root, apiBase, {});
      return;
    }

    submitBtn.disabled = true;
    submitBtn.textContent = '';
    const spinnerEl = document.createElement('span');
    spinnerEl.className = 'gs-spinner';
    submitBtn.appendChild(spinnerEl);
    submitBtn.appendChild(document.createTextNode(' Submitting…'));
    submitBanner.style.display = 'none';

    try {
      const resp = await fetch(`${apiBase}/api/gallery/submissions/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ review_token: _reviewToken, reviews }),
      });

      const json = await resp.json().catch(() => ({ success: false, error: 'Invalid server response' }));

      if (!resp.ok || !json.success) {
        throw new Error(json.error || `Server error (${resp.status})`);
      }

      renderStep3(root, apiBase, json);

    } catch (err) {
      console.error('[GallerySubmit] step2 error:', err);
      showBanner(submitBanner, err.message || 'Something went wrong. Please try again.', 'error');
      submitBtn.disabled    = false;
      submitBtn.textContent = 'Submit Review';
    }
  }

  // ============================================================
  // STEP 3 — Done
  // ============================================================
  function renderStep3(root, apiBase, json) {
    clearEl(root);

    // No step progress indicator — intentionally omitted

    const successWrap = el('div', { class: 'gs-success-state' });

    // Genuine thank-you shown on EVERY path — above everything else
    const thankIcon = el('div', { class: 'gs-success-icon', 'aria-hidden': 'true' });
    thankIcon.textContent = '🎉';
    const thankTitle = el('h2', { class: 'gs-step-title' });
    thankTitle.textContent = 'Thank you so much!';
    const thankSub = el('p', { class: 'gs-step-sub gs-thank-sub' });
    thankSub.textContent = 'We genuinely appreciate you taking the time to share your build with the TFS Wheels community. Your submission is now in our queue — once approved, it\'ll be featured in the gallery for everyone to see.';
    successWrap.appendChild(thankIcon);
    successWrap.appendChild(thankTitle);
    successWrap.appendChild(thankSub);

    if (json && json.show_google_ask === true) {
      // Google ask path — appreciation already shown above, now add the ask below
      renderGoogleAsk(successWrap, json, apiBase);
    }
    // Non-eligible path: no additional content needed — thank-you is the full message

    // Change D: sweepstakes confirmation (safe DOM construction — no innerHTML)
    const sweepLine = el('p', { class: 'gs-step-sub' });
    sweepLine.appendChild(document.createTextNode("You're entered in this month's "));
    const sweepStrong = el('strong');
    sweepStrong.textContent = '$200 draw';
    sweepLine.appendChild(sweepStrong);
    sweepLine.appendChild(document.createTextNode('. '));
    const rulesLink = el('a', { href: '/pages/sweepstakes-rules', target: '_blank', rel: 'noopener' });
    rulesLink.textContent = 'Official Rules';
    sweepLine.appendChild(rulesLink);
    sweepLine.appendChild(document.createTextNode('.'));
    successWrap.appendChild(sweepLine);

    root.appendChild(successWrap);
    scrollFormToTop(root);
  }

  // ============================================================
  // Google review ask block
  // ============================================================
  function renderGoogleAsk(container, json, apiBase) {
    const publicId   = json.public_id || _publicId || null;
    const googleUrl  = (json.google_review_url || '').trim() || null;
    const storeText  = (json.store_review_text  || '').trim() || '';
    const incentive  = json.incentive || null;

    const askBlock = el('div', { class: 'gs-google-ask' });

    // Heading for the Google ask section
    const askHeading = el('p', { class: 'gs-google-ask-heading' });
    askHeading.textContent = 'One more thing — would you mind leaving us a Google review?';
    askBlock.appendChild(askHeading);

    const askDesc = el('p', { class: 'gs-google-ask-desc' });
    askDesc.textContent = 'It makes a huge difference for a small business like ours.';
    askBlock.appendChild(askDesc);

    // Incentive callout — show both amounts with clear copy
    if (incentive && incentive.enabled) {
      const incentiveP = el('div', { class: 'gs-google-ask-incentive' });
      const photoCents = parseInt(incentive.photo_cents, 10) || 0;
      const baseCents  = parseInt(incentive.base_cents,  10) || 0;
      let amountText;
      if (baseCents > 0 && photoCents > 0 && photoCents !== baseCents) {
        const baseDollars  = (baseCents  / 100).toFixed(0);
        const photoDollars = (photoCents / 100).toFixed(0);
        amountText = 'Leave a Google review for a $' + baseDollars + ' store credit — or $' + photoDollars + ' if you include photos.';
      } else if (photoCents > 0) {
        const dollars = (photoCents / 100).toFixed(0);
        amountText = 'Leave a Google review and earn $' + dollars + ' store credit!';
      } else if (baseCents > 0) {
        const dollars = (baseCents / 100).toFixed(0);
        amountText = 'Leave a Google review for a $' + dollars + ' store credit!';
      } else {
        amountText = 'Leave a Google review and earn store credit!';
      }
      incentiveP.textContent = amountText;
      askBlock.appendChild(incentiveP);
    }

    // Store review text — the customer's OWN submitted review shown back for copy-paste
    if (storeText) {
      const textLabel = el('p', { class: 'gs-google-ask-text-label' });
      textLabel.textContent = 'Here\'s your review — copy & paste it on Google:';
      askBlock.appendChild(textLabel);

      const textBox = el('blockquote', { class: 'gs-google-ask-review-text' });
      textBox.textContent = storeText;
      askBlock.appendChild(textBox);

      const copyBtn = el('button', { type: 'button', class: 'gs-google-ask-copy-btn' });
      copyBtn.textContent = 'Copy to Clipboard';
      copyBtn.addEventListener('click', function () {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(storeText).then(function () {
            copyBtn.textContent = 'Copied!';
            setTimeout(function () { copyBtn.textContent = 'Copy to Clipboard'; }, 2500);
          }).catch(function () { fallbackCopy(storeText, copyBtn); });
        } else {
          fallbackCopy(storeText, copyBtn);
        }
      });
      askBlock.appendChild(copyBtn);
    }

    // Google review link button — validate URL before using as href
    // Only http: and https: are permitted; any other scheme (javascript:, data:, etc.) is rejected.
    let safeGoogleUrl = null;
    if (googleUrl) {
      try {
        const parsed = new URL(googleUrl);
        if (parsed.protocol === 'https:' || parsed.protocol === 'http:') {
          safeGoogleUrl = parsed.href;
        }
      } catch (_e) {
        // malformed URL — fall through to "link coming soon" path
      }
    }

    if (safeGoogleUrl) {
      const googleBtn = el('a', {
        href: safeGoogleUrl,
        target: '_blank',
        rel: 'noopener noreferrer',
        class: 'gs-google-ask-link-btn',
      });
      const linkIcon = el('span', { 'aria-hidden': 'true' });
      linkIcon.textContent = 'G';
      googleBtn.appendChild(linkIcon);
      googleBtn.appendChild(document.createTextNode(' Leave a Google Review'));

      googleBtn.addEventListener('click', function () {
        if (!publicId) return;
        fetch(apiBase + '/api/gallery/google-clicked', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ public_id: publicId }),
        }).catch(function () {});
      });

      askBlock.appendChild(googleBtn);
    } else {
      const comingNote = el('p', { class: 'gs-google-ask-coming-note' });
      comingNote.textContent = 'Our Google review link will be available soon. You can still copy your review text above and paste it when it arrives.';
      askBlock.appendChild(comingNote);
    }

    // Final note
    const finalNote = el('p', { class: 'gs-google-ask-final-note' });
    finalNote.textContent = 'Either way, your build is safe and in our queue.';
    askBlock.appendChild(finalNote);

    container.appendChild(askBlock);
  }

  // Clipboard fallback
  function fallbackCopy(text, btn) {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.top = '-9999px';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    let ok = false;
    try { ok = document.execCommand('copy'); } catch (e) { ok = false; }
    document.body.removeChild(ta);
    btn.textContent = ok ? 'Copied!' : 'Copy failed — please copy manually';
    setTimeout(function () { btn.textContent = 'Copy to Clipboard'; }, 2500);
  }

  // ============================================================
  // File handling
  // ============================================================
  function handleFileSelect(newFiles, thumbGrid, thumbCount, fileInput) {
    const valid  = [];
    const errors = [];

    for (const f of newFiles) {
      if (!ALLOWED_MIMES.has(f.type)) {
        errors.push('"' + f.name + '" is not a supported image type.');
        continue;
      }
      if (f.size > MAX_FILE_BYTES) {
        errors.push('"' + f.name + '" exceeds the 15 MB limit.');
        continue;
      }
      if (_selectedFiles.length + valid.length >= MAX_FILES) {
        errors.push('Maximum ' + MAX_FILES + ' photos allowed.');
        break;
      }
      valid.push({ file: f, previewUrl: URL.createObjectURL(f) });
    }

    if (errors.length) {
      let errEl = document.getElementById('gs-file-errors');
      if (!errEl) {
        errEl = el('div', { id: 'gs-file-errors', class: 'gs-banner gs-banner-error', style: 'margin-top:8px' });
        thumbGrid.parentElement?.insertBefore(errEl, thumbGrid);
      }
      errEl.textContent = errors.join(' ');
      errEl.style.display = '';
    } else {
      const errEl = document.getElementById('gs-file-errors');
      if (errEl) errEl.style.display = 'none';
    }

    _selectedFiles = _selectedFiles.concat(valid);
    renderThumbs(thumbGrid, thumbCount);
    fileInput.value = '';
  }

  function renderThumbs(thumbGrid, thumbCount) {
    clearEl(thumbGrid);

    for (let i = 0; i < _selectedFiles.length; i++) {
      const { previewUrl, file } = _selectedFiles[i];
      const thumb = el('div', { class: 'gs-thumb' });
      const img   = el('img', { src: previewUrl, alt: esc(file.name), loading: 'lazy' });
      const btn   = el('button', { type: 'button', class: 'gs-thumb-remove', 'aria-label': `Remove ${esc(file.name)}` });
      btn.textContent = '×'; // multiplication sign
      const idx = i;
      btn.addEventListener('click', () => {
        URL.revokeObjectURL(_selectedFiles[idx].previewUrl);
        _selectedFiles.splice(idx, 1);
        renderThumbs(thumbGrid, thumbCount);
      });
      thumb.appendChild(img);
      thumb.appendChild(btn);
      thumbGrid.appendChild(thumb);
    }

    thumbCount.textContent = _selectedFiles.length > 0
      ? `${_selectedFiles.length} photo${_selectedFiles.length !== 1 ? 's' : ''} selected`
      : '';
  }

  // ============================================================
  // Turnstile
  // ============================================================
  function loadTurnstile(sitekey, container, submitBtn, form, consentReview, consentUgc) {
    if (typeof window.turnstile !== 'undefined') {
      mountTurnstile(sitekey, container, submitBtn, form, consentReview, consentUgc);
      return;
    }

    if (!document.getElementById('cf-turnstile-script')) {
      const script = document.createElement('script');
      script.id    = 'cf-turnstile-script';
      script.src   = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=_gsTurnstileReady';
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }

    window._gsTurnstileReady = function () {
      mountTurnstile(sitekey, container, submitBtn, form, consentReview, consentUgc);
    };
  }

  function mountTurnstile(sitekey, container, submitBtn, form, consentReview, consentUgc) {
    if (typeof window.turnstile === 'undefined') return;
    if (_turnstileWidgetId !== null) return;

    _turnstileWidgetId = window.turnstile.render(container, {
      sitekey,
      callback: (token) => {
        _turnstileToken = token;
        liveValidateStep1(form, submitBtn, consentReview, consentUgc);
      },
      'expired-callback': () => {
        _turnstileToken = null;
        liveValidateStep1(form, submitBtn, consentReview, consentUgc);
      },
      'error-callback': () => {
        _turnstileToken = null;
      },
    });
  }

  // ============================================================
  // Bootstrap
  // ============================================================
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
